(function () {
  'use strict';

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  class PMSMissionaryBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      if (this.querySelector('[data-pms-plan-result]')) return;
      this.initialize().catch((error) => this.showFatal(error));
    }

    readJson(selector) {
      const node = this.querySelector(selector);
      if (!node) return null;
      try { return JSON.parse(node.textContent); }
      catch (error) { console.error('[PMS Missionary Builder] Invalid JSON.', error); return null; }
    }

    async initialize() {
      this.core = window.PMSMissionaryCore;
      this.Adapter = window.PMSMissionaryShopifyAdapter;
      if (!this.core || !this.Adapter) throw new Error('The builder assets did not load.');

      this.config = this.readJson('[data-pms-missionary-config]') || {};
      this.adapter = new this.Adapter(this, this.config);
      const rawCatalog = await this.adapter.loadCatalog();
      this.catalog = {
        ...rawCatalog,
        suitPrograms: rawCatalog.suitPrograms.map((program) => this.core.createSuitProgram(program)).filter(Boolean),
      };
      this.detailProducts = [
        ...this.catalog.suitPrograms.flatMap((program) => [program.coat, program.pant]),
        ...['slacks', 'traditionalShirts', 'stretchShirts', 'ties', 'socks', 'buckles', 'straps'].flatMap((key) => this.catalog[key] || []),
        this.catalog.sewingKit, this.catalog.careBooklet,
      ].filter(Boolean);

      const requestedPackageCode = new URLSearchParams(window.location.search).get('package');
      const requestedPackage = Object.prototype.hasOwnProperty.call(this.core.PACKAGE_DEFINITIONS, requestedPackageCode)
        ? this.core.PACKAGE_DEFINITIONS[requestedPackageCode] : null;
      const initialPackageCode = requestedPackage?.enabled
        ? requestedPackageCode : (this.config.packageCode || 'all-season-stretch');
      const initialPackage = this.core.PACKAGE_DEFINITIONS[initialPackageCode] || this.core.PACKAGE_DEFINITIONS['all-season-stretch'];
      this.packageDefinition = {
        ...initialPackage,
        targetPrice: this.packagePrice(initialPackage.code),
      };
      this.state = this.createInitialState();
      this.missions = Array.isArray(window.PMSMissionList) ? window.PMSMissionList : [];
      this.stepIndex = 0;
      this.tieSearch = '';
      this.copyFallback = '';
      this.status = '';
      this.statusError = false;
      this.app = this.querySelector('[data-pms-missionary-app]');
      if (!this.app) throw new Error('The builder application container is missing.');
      this.bindEvents();
      this.render();
    }

    createInitialState() {
      return {
        suits: Array.from({ length: this.packageDefinition.suitCount }, () => ({
          programId: '', colorKey: '', colorLabel: '', coatVariantId: '', pantVariantId: '',
        })),
        slackWaist: '',
        slacks: {},
        shirtSize: '',
        traditionalFit: '',
        traditionalCollar: '',
        traditionalSleeve: '',
        shirts: {},
        ties: {},
        sockSize: '',
        socks: {},
        belts: Array.from({ length: this.packageDefinition.belts }, () => ({
          buckleVariantId: '', strapVariantId: '', sizing: 'uncut', cutSize: '', acknowledged: false,
        })),
        shortageAcknowledged: false,
        customer: { mission: '', name: '', contactName: '', phone: '', email: '' },
        handoffConsent: false,
        handoffSubmitted: false,
        handoffConfirmed: false,
        handoffReference: '',
        handoffError: '',
      };
    }


    packagePrice(code) {
      const mapped = Number(this.config.packagePrices?.[code]);
      if (mapped > 0) return mapped;
      if (code === this.config.packageCode && Number(this.config.targetPrice) > 0) return Number(this.config.targetPrice);
      return Number(this.core.PACKAGE_DEFINITIONS[code]?.targetPrice) || 0;
    }

    enabledPackageDefinitions() {
      return Object.values(this.core.PACKAGE_DEFINITIONS)
        .filter((item) => item.enabled)
        .map((item) => ({ ...item, targetPrice: this.packagePrice(item.code) }));
    }

    packageClimateKey(code = this.packageDefinition.code) {
      if (String(code).startsWith('warm-weather-')) return 'warm-weather';
      if (String(code).startsWith('hot-weather-')) return 'hot-weather';
      return 'all-season';
    }

    packageCodeFor(climate, shirtFamily) {
      const code = `${climate}-${shirtFamily}`;
      const definition = this.core.PACKAGE_DEFINITIONS[code];
      return definition?.enabled ? code : this.packageDefinition.code;
    }

    renderPackageIntroduction() {
      const climate = this.packageClimateKey();
      const shirtFamily = this.packageDefinition.shirtFamily === 'traditional' ? 'traditional' : 'stretch';
      const climates = [
        { key: 'all-season', eyebrow: 'Changing seasons', title: 'All-Season', copy: 'For missions with meaningful seasonal changes, from cold winters to warm summers. Includes two suits.' },
        { key: 'warm-weather', eyebrow: 'Mostly warm', title: 'Warm Weather', copy: 'For warmer destinations where one suit is still useful or required, with more warm-weather essentials.' },
        { key: 'hot-weather', eyebrow: 'Consistently hot', title: 'Hot Weather', copy: 'For hot climates where lightweight essentials do the work and a suit is not normally required.' },
      ];
      const styles = [
        { key: 'traditional', title: 'Traditional shirts', copy: 'Easy-care cotton/poly dress shirts' },
        { key: 'stretch', title: 'Stretch shirts', copy: 'Four-way stretch performance shirts' },
      ];

      return `<section class="pms-missionary__package-intro">
        <div class="pms-missionary__package-intro-copy">
          <p>Choose your package</p>
          <h2>Start with the climate. Then choose your shirt style.</h2>
          <div>These recommendations are a helpful starting point, not a restriction. Choose the package that best fits your mission and preferences.</div>
          <a class="pms-missionary__compare-link" href="/pages/missionary-packages">Want the whole picture first? Compare all package details</a>
        </div>
        <fieldset class="pms-missionary__package-group">
          <legend>1. Which climate best describes your mission?</legend>
          <div class="pms-missionary__climate-grid">
            ${climates.map((item) => {
              const code = this.packageCodeFor(item.key, shirtFamily);
              const selected = item.key === climate;
              return `<button type="button" class="pms-missionary__climate-card${selected ? ' is-selected' : ''}" data-action="choose-package" data-package-code="${escapeHtml(code)}" aria-pressed="${selected}">
                <span>${escapeHtml(item.eyebrow)}</span>
                <strong>${escapeHtml(item.title)}</strong>
                <small>${escapeHtml(item.copy)}</small>
              </button>`;
            }).join('')}
          </div>
        </fieldset>
        <fieldset class="pms-missionary__package-group">
          <legend>2. Which shirt style do you prefer?</legend>
          <div class="pms-missionary__shirt-style-grid">
            ${styles.map((item) => {
              const code = this.packageCodeFor(climate, item.key);
              const selected = item.key === shirtFamily;
              return `<button type="button" class="pms-missionary__shirt-style${selected ? ' is-selected' : ''}" data-action="choose-package" data-package-code="${escapeHtml(code)}" aria-pressed="${selected}">
                <span><strong>${escapeHtml(item.title)}</strong><small>${escapeHtml(item.copy)}</small></span>
                <b>${this.money(this.packagePrice(code))}</b>
              </button>`;
            }).join('')}
          </div>
        </fieldset>
        <div class="pms-missionary__package-selected" aria-live="polite">
          <span>Your selection</span>
          <strong>${escapeHtml(this.packageDefinition.title)}</strong>
          <b>${this.money(this.packageDefinition.targetPrice)}</b>
        </div>
        <details class="pms-missionary__package-fallback">
          <summary>Prefer one simple list? Choose from all six packages</summary>
          ${this.selectField('package-code', 'Missionary package', this.enabledPackageDefinitions().map((item) => ({ value: item.code, label: `${item.title} — ${this.money(item.targetPrice)}` })), this.packageDefinition.code)}
        </details>
      </section>`;
    }


    switchPackage(code) {
      const next = this.core.PACKAGE_DEFINITIONS[code];
      if (!next || !next.enabled || next.code === this.packageDefinition.code) return;
      const customer = { ...(this.state?.customer || {}) };
      this.packageDefinition = { ...next, targetPrice: this.packagePrice(next.code) };
      this.state = this.createInitialState();
      this.state.customer = customer;
      this.stepIndex = 0;
      this.tieSearch = '';
      this.copyFallback = '';
      this.status = '';
      this.statusError = false;
      this.render();
    }

    get steps() {
      const list = [];
      for (let i = 0; i < this.packageDefinition.suitCount; i += 1) {
        list.push({ key: `suit-${i + 1}`, label: `Suit ${i + 1}`, type: 'suit', index: i });
      }
      list.push(
        { key: 'slacks', label: 'Golf slacks', type: 'slacks' },
        { key: 'shirts', label: this.packageDefinition.shirtFamily === 'traditional' ? 'Traditional shirts' : 'Stretch shirts', type: 'shirts' },
        { key: 'ties', label: 'Ties', type: 'ties' },
        { key: 'socks', label: 'Socks', type: 'socks' },
      );
      for (let i = 0; i < this.packageDefinition.belts; i += 1) {
        list.push({ key: `belt-${i + 1}`, label: `Belt ${i + 1}`, type: 'belt', index: i });
      }
      list.push({ key: 'review', label: 'Review', type: 'review' });
      return list;
    }

    bindEvents() {
      this.handoffMessageHandler = (event) => {
        if (event.origin !== window.location.origin || event.source !== this.handoffWindow || !this.handoffWindow) return;
        if (event.data?.type !== 'pms-plan-result') return;
        if (event.data.result === 'success') {
          this.state.handoffConfirmed = true;
          this.setStatus(this.config.handoffLabels.sentTitle);
          this.syncHandoffVisualState();
        } else if (event.data.result === 'error') {
          this.showHandoffError(this.config.handoffLabels.errorBody);
        }
      };
      window.addEventListener('message', this.handoffMessageHandler);
      this.app.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-action]');
        if (!button) return;
        const action = button.dataset.action;
        if (action === 'product-details') this.openProductDetails(button);
        if (action === 'next') this.goNext();
        if (action === 'back') this.goBack();
        if (action === 'goto-step') this.goToStep(Number(button.dataset.step));
        if (action === 'quantity') this.changeQuantity(button.dataset.group, button.dataset.variantId, Number(button.dataset.delta));
        if (action === 'print') this.printPlan();
        if (action === 'copy-summary') this.copyPackageSummary();
        if (action === 'select-summary') this.selectSummaryFallback();
        if (action === 'send-plan') this.sendPackagePlan();
        if (action === 'purchase') this.purchasePackage();
        if (action === 'clear-test-cart') this.clearTestCart();
        if (action === 'choose-package') this.switchPackage(button.dataset.packageCode);
      });

      this.app.addEventListener('change', (event) => {
        const target = event.target;
        const field = target.dataset.field;
        if (!field) return;
        this.handleFieldChange(field, target);
      });

      this.app.addEventListener('input', (event) => {
        const target = event.target;
        if (target.dataset.field === 'tie-search') {
          this.tieSearch = target.value;
          const cursor = target.selectionStart;
          this.renderStepOnly();
          const replacement = this.app.querySelector('[data-field="tie-search"]');
          if (replacement) {
            replacement.focus();
            replacement.setSelectionRange(cursor, cursor);
          }
        }
        if (target.dataset.customerField) {
          this.state.customer[target.dataset.customerField] = target.value;
          this.state.handoffSubmitted = false;
          this.updatePrintSummary();
          this.syncHandoffVisualState();
        }
      });
    }

    handleFieldChange(field, target) {
      if (field === 'package-code') {
        this.switchPackage(target.value);
        return;
      }
      if (field === 'handoff-consent') {
        this.state.handoffConsent = target.checked;
        this.state.handoffSubmitted = false;
      } else if (field === 'shortage-ack') {
        this.state.shortageAcknowledged = target.checked;
      } else if (field.startsWith('suit-program-')) {
        this.state.shortageAcknowledged = false;
        const index = Number(field.split('-').pop());
        this.state.suits[index] = { programId: target.value, colorKey: '', colorLabel: '', coatVariantId: '', pantVariantId: '' };
        this.ensureSuitDefaults(index);
      } else if (field.startsWith('suit-color-')) {
        this.state.shortageAcknowledged = false;
        const index = Number(field.split('-').pop());
        const selection = this.state.suits[index];
        selection.colorKey = target.value;
        const program = this.getSuitProgram(selection.programId);
        selection.colorLabel = this.core.suitColorChoices(program).find((item) => item.key === target.value)?.label || '';
        selection.coatVariantId = '';
        selection.pantVariantId = '';
        this.ensureSuitDefaults(index);
      } else if (field.startsWith('suit-coat-')) {
        this.state.shortageAcknowledged = false;
        this.state.suits[Number(field.split('-').pop())].coatVariantId = target.value;
      } else if (field.startsWith('suit-pant-')) {
        this.state.shortageAcknowledged = false;
        this.state.suits[Number(field.split('-').pop())].pantVariantId = target.value;
      } else if (field === 'slack-waist') {
        this.state.shortageAcknowledged = false;
        this.state.slackWaist = target.value;
        this.state.slacks = {};
      } else if (field === 'shirt-size') {
        this.state.shortageAcknowledged = false;
        this.state.shirtSize = target.value;
        this.state.shirts = {};
      } else if (field === 'traditional-fit') {
        this.state.shortageAcknowledged = false;
        this.state.traditionalFit = target.value;
        this.state.traditionalCollar = '';
        this.state.traditionalSleeve = '';
        this.state.shirts = {};
      } else if (field === 'traditional-collar') {
        this.state.shortageAcknowledged = false;
        this.state.traditionalCollar = target.value;
        this.state.traditionalSleeve = '';
        this.state.shirts = {};
      } else if (field === 'traditional-sleeve') {
        this.state.shortageAcknowledged = false;
        this.state.traditionalSleeve = target.value;
        this.state.shirts = {};
      } else if (field === 'sock-size') {
        this.state.shortageAcknowledged = false;
        this.state.sockSize = target.value;
        this.state.socks = {};
      } else if (field.startsWith('belt-buckle-')) {
        this.state.shortageAcknowledged = false;
        this.state.belts[Number(field.split('-').pop())].buckleVariantId = target.value;
      } else if (field.startsWith('belt-strap-')) {
        this.state.shortageAcknowledged = false;
        this.state.belts[Number(field.split('-').pop())].strapVariantId = target.value;
      } else if (field.startsWith('belt-sizing-')) {
        const belt = this.state.belts[Number(field.split('-').pop())];
        belt.sizing = target.value;
        if (target.value !== 'cut') { belt.cutSize = ''; belt.acknowledged = false; }
      } else if (field.startsWith('belt-size-')) {
        this.state.belts[Number(field.split('-').pop())].cutSize = target.value;
      } else if (field.startsWith('belt-ack-')) {
        this.state.belts[Number(field.split('-').pop())].acknowledged = target.checked;
      }
      this.clearStatus();
      this.renderStepOnly();
    }

    getSuitProgram(id) {
      return this.catalog.suitPrograms.find((program) => program.id === id) || null;
    }

    inventoryReport() {
      return this.core.inventoryReport({ packageDefinition: this.packageDefinition, state: this.state, catalog: this.catalog });
    }

    demandForVariant(variantId) {
      return this.inventoryReport().lines.find((line) => String(line.variantId) === String(variantId))?.requested || 0;
    }

    candidateInventory(variant, newQuantity, currentVariantId = '', currentQuantity = 0) {
      if (!variant) return this.core.inventoryStatus(null, newQuantity);
      let existing = this.demandForVariant(variant.id);
      if (String(currentVariantId) === String(variant.id)) existing = Math.max(0, existing - Number(currentQuantity || 0));
      return this.core.inventoryStatus(variant, existing + Number(newQuantity || 0));
    }

    inventoryNote(variant, requested = 0) {
      const status = this.core.inventoryStatus(variant, requested);
      return this.inventoryStatusText(status);
    }

    inventoryStatusText(status) {
      if (!status.tracked) return '';
      if (status.toOrder > 0) return `${status.availableNow} available now - ${status.toOrder} to be ordered`;
      if (status.onHand <= 0 && status.canSpecialOrder) return 'Available by special order';
      return `${status.onHand} available now`;
    }

    ensureSuitDefaults(index) {
      const selection = this.state.suits[index];
      if (!selection.programId && this.catalog.suitPrograms.length) selection.programId = this.catalog.suitPrograms[0].id;
      const program = this.getSuitProgram(selection.programId);
      if (!program) return;
      const colors = this.core.suitColorChoices(program);
      if (!selection.colorKey || !colors.some((item) => item.key === selection.colorKey && item.available)) {
        const firstColor = colors.find((item) => item.available) || colors[0];
        selection.colorKey = firstColor?.key || '';
        selection.colorLabel = firstColor?.label || '';
      }
      const coats = this.core.suitVariants(program, 'coat', selection.colorKey);
      const pants = this.core.suitVariants(program, 'pant', selection.colorKey);
      const coatAllowed = (variant) => this.candidateInventory(variant, 1, selection.coatVariantId, 1).allowed;
      const pantAllowed = (variant) => this.candidateInventory(variant, 2, selection.pantVariantId, 2).allowed;
      const coat = coats.find((variant) => variant.id === selection.coatVariantId && coatAllowed(variant)) || coats.find(coatAllowed);
      const pant = pants.find((variant) => variant.id === selection.pantVariantId && pantAllowed(variant)) || pants.find(pantAllowed);
      selection.coatVariantId = coat?.id || '';
      selection.pantVariantId = pant?.id || '';
    }

    render() {
      this.steps.forEach((step) => {
        if (step.type === 'suit') this.ensureSuitDefaults(step.index);
      });
      this.app.innerHTML = `
        ${this.renderPackageIntroduction()}
        <section class="pms-missionary__personalization">
          <div>
            <p>Make it yours <span>Optional</span></p>
            <h2>Where will you be serving?</h2>
            <div>Choose your mission now and we will carry it through to your final package plan.</div>
          </div>
          <label class="pms-missionary__field pms-missionary__mission-field">
            <span>Mission assignment</span>
            <input type="text" data-customer-field="mission" list="pms-mission-list-${escapeHtml(this.config.sectionId || 'builder')}" value="${escapeHtml(this.state.customer.mission)}" placeholder="Start typing a mission name" autocomplete="off">
            <small>Not listed yet? You can type the mission name exactly as it appears on the call.</small>
          </label>
          <datalist id="pms-mission-list-${escapeHtml(this.config.sectionId || 'builder')}">
            ${this.missions.map((mission) => `<option value="${escapeHtml(mission)}"></option>`).join('')}
          </datalist>
        </section>
        <div class="pms-missionary__shell">
          <aside class="pms-missionary__progress" aria-label="Package progress">
            <div class="pms-missionary__package-chip">
              <span>Selected package</span>
              <strong>${escapeHtml(this.packageDefinition.title)}</strong>
              <b>${this.money(this.packageDefinition.targetPrice)}</b>
            </div>
            ${this.config.designMode ? `<div class="pms-missionary__test-tools"><strong>Theme-preview tools</strong><button type="button" class="button button--secondary" data-action="clear-test-cart">Clear Preview Cart</button><small>Clears only this browser's current test cart.</small></div>` : ''}
            <ol>${this.steps.map((step, index) => this.renderProgressStep(step, index)).join('')}</ol>
          </aside>
          <main class="pms-missionary__main">
            <div data-pms-step></div>
          </main>
        </div>
        <section class="pms-missionary__print" data-pms-print aria-hidden="true"></section>
      `;
      this.stepContainer = this.app.querySelector('[data-pms-step]');
      this.printContainer = this.app.querySelector('[data-pms-print]');
      this.renderStepOnly();
    }

    renderProgressStep(step, index) {
      const complete = this.isStepComplete(step);
      const active = index === this.stepIndex;
      return `<li class="${active ? 'is-active' : ''} ${complete ? 'is-complete' : ''}">
        <button type="button" data-action="goto-step" data-step="${index}" ${index > this.stepIndex && !complete ? 'disabled' : ''}>
          <span>${complete ? '✓' : index + 1}</span><b>${escapeHtml(step.label)}</b>
        </button>
      </li>`;
    }

    renderStepOnly() {
      if (!this.stepContainer) return;
      const step = this.steps[this.stepIndex];
      let content = '';
      if (step.type === 'suit') content = this.renderSuitStep(step.index);
      if (step.type === 'slacks') content = this.renderSlacksStep();
      if (step.type === 'shirts') content = this.renderShirtsStep();
      if (step.type === 'ties') content = this.renderTiesStep();
      if (step.type === 'socks') content = this.renderSocksStep();
      if (step.type === 'belt') content = this.renderBeltStep(step.index);
      if (step.type === 'review') content = this.renderReviewStep();
      this.stepContainer.innerHTML = content;
      this.refreshProgress();
      this.updatePrintSummary();
    }

    refreshProgress() {
      const list = this.app.querySelector('.pms-missionary__progress ol');
      if (list) list.innerHTML = this.steps.map((step, index) => this.renderProgressStep(step, index)).join('');
    }

    stepHeader(kicker, title, copy) {
      return `<header class="pms-missionary__step-header">
        <p>${escapeHtml(kicker)}</p><h2>${escapeHtml(title)}</h2><div>${escapeHtml(copy)}</div>
      </header>`;
    }

    renderSuitStep(index) {
      const selection = this.state.suits[index];
      const programs = this.catalog.suitPrograms;
      const program = this.getSuitProgram(selection.programId);
      const colors = program ? this.core.suitColorChoices(program) : [];
      const coats = program ? this.core.suitVariants(program, 'coat', selection.colorKey) : [];
      const pants = program ? this.core.suitVariants(program, 'pant', selection.colorKey) : [];
      const coatOptions = coats.map((variant) => {
        const status = this.candidateInventory(variant, 1, selection.coatVariantId, 1);
        const note = this.inventoryStatusText(status);
        return { value: variant.id, label: `${variant._pmsSize}${note ? ` - ${note}` : ''}`, disabled: !status.allowed };
      });
      const pantOptions = pants.map((variant) => {
        const status = this.candidateInventory(variant, 2, selection.pantVariantId, 2);
        const note = this.inventoryStatusText(status);
        return { value: variant.id, label: `${variant._pmsSize}${note ? ` - ${note}` : ''}`, disabled: !status.allowed };
      });
      const selectedCoat = coats.find((variant) => String(variant.id) === String(selection.coatVariantId)) || coats[0] || null;
      const selectedPant = pants.find((variant) => String(variant.id) === String(selection.pantVariantId)) || pants[0] || null;
      const suitImage = selectedCoat?.image || program?.image || program?.coat?.featuredImage || '';
      const suitLabel = [program?.title, selection.colorLabel].filter(Boolean).join(' · ');
      const suitPreview = suitImage
        ? `<figure class="pms-missionary__suit-preview">
            <button type="button" class="pms-missionary__photo-button" ${this.detailsAttributes(program.coat, selectedCoat)} aria-label="${escapeHtml(this.detailLabel('view', 'View details'))}: ${escapeHtml(suitLabel)}"><img src="${escapeHtml(suitImage)}" alt="${escapeHtml(suitLabel || `Suit ${index + 1}`)}" loading="eager"></button>
            <figcaption><strong>${escapeHtml(suitLabel || `Suit ${index + 1}`)}</strong><span>Selected suit preview</span><div class="pms-missionary__suit-details">${this.detailsButton(program.coat, selectedCoat, this.detailLabel('coat', 'Coat photos and details'))}${this.detailsButton(program.pant, selectedPant, this.detailLabel('pant', 'Pant photos and details'))}</div></figcaption>
          </figure>`
        : '';

      return `${this.stepHeader(`Suit ${index + 1} of ${this.packageDefinition.suitCount}`, `Build Suit ${index + 1}`, 'Each missionary suit includes one coat and two matching pants in the same color and waist size.')}
        ${suitPreview}
        <div class="pms-missionary__form-grid">
          ${this.selectField(`suit-program-${index}`, 'Suit program', programs.map((item) => ({ value: item.id, label: item.title })), selection.programId)}
          ${this.selectField(`suit-color-${index}`, 'Color', colors.map((item) => ({ value: item.key, label: item.label, disabled: !item.available })), selection.colorKey)}
          ${this.selectField(`suit-coat-${index}`, 'Coat size', coatOptions, selection.coatVariantId)}
          ${this.selectField(`suit-pant-${index}`, 'Pant waist', pantOptions, selection.pantVariantId)}
        </div>
        <div class="pms-missionary__callout"><strong>Two-pant suit:</strong> The selected pant variant will be added with quantity 2 automatically.</div>
        ${this.navigation()}`;
    }

    renderSlacksStep() {
      const required = this.packageDefinition.slacks;
      const waists = this.core.getSlackWaists(this.catalog.slacks);
      const choices = this.state.slackWaist ? this.core.getSlackChoices(this.catalog.slacks, this.state.slackWaist) : [];
      return `${this.stepHeader('Washable golf slacks', `Choose ${required} slacks`, 'Pick one waist size, then distribute the required quantity among the available fits and colors.')}
        ${this.selectField('slack-waist', 'Waist size for all slacks', waists.map((value) => ({ value, label: value })), this.state.slackWaist)}
        ${this.counter(required, this.core.sumAllocation(this.state.slacks), 'slacks')}
        <div class="pms-missionary__choice-grid">${choices.map((choice) => this.quantityCard('slacks', choice, required)).join('')}</div>
        ${this.navigation()}`;
    }

    renderShirtsStep() {
      return this.packageDefinition.shirtFamily === 'traditional'
        ? this.renderTraditionalShirtsStep()
        : this.renderStretchShirtsStep();
    }

    renderStretchShirtsStep() {
      const required = this.packageDefinition.shirts;
      const sizes = this.core.getStretchSizeFamilies(this.catalog.stretchShirts);
      const choices = this.state.shirtSize ? this.core.getStretchShirtChoices(this.catalog.stretchShirts, this.state.shirtSize, true) : [];
      const grouped = ['Long sleeve', 'Long sleeve tall', 'Short sleeve'].map((sleeve) => ({ sleeve, choices: choices.filter((item) => item.sleeve === sleeve) }));
      return `${this.stepHeader('Four-way stretch shirts', `Choose ${required} white shirts`, 'Choose one size family, then mix brands, fits, and sleeve types. You are allocating quantities—not sizing ten shirts individually.')}
        ${this.selectField('shirt-size', 'Size family for all shirts', sizes.map((value) => ({ value, label: value })), this.state.shirtSize)}
        ${this.counter(required, this.core.sumAllocation(this.state.shirts), 'shirts')}
        ${grouped.map((group) => group.choices.length ? `<section class="pms-missionary__subgroup"><h3>${escapeHtml(group.sleeve)}</h3><div class="pms-missionary__choice-grid">${group.choices.map((choice) => this.quantityCard('shirts', choice, required)).join('')}</div></section>` : '').join('')}
        ${this.navigation()}`;
    }

    renderTraditionalShirtsStep() {
      const required = this.packageDefinition.shirts;
      const products = this.catalog.traditionalShirts;
      const fits = this.core.getTraditionalFits(products, true);
      const collars = this.state.traditionalFit ? this.core.getTraditionalCollars(products, this.state.traditionalFit, true) : [];
      const sleeves = this.state.traditionalFit && this.state.traditionalCollar
        ? this.core.getTraditionalSleeveLengths(products, this.state.traditionalFit, this.state.traditionalCollar, true)
        : [];
      const choices = this.state.traditionalFit && this.state.traditionalCollar && this.state.traditionalSleeve
        ? this.core.getTraditionalShirtChoices(products, {
            fit: this.state.traditionalFit,
            collar: this.state.traditionalCollar,
            longSleeve: this.state.traditionalSleeve,
            whiteOnly: true,
          })
        : [];
      const grouped = ['Long sleeve', 'Short sleeve'].map((sleeve) => ({ sleeve, choices: choices.filter((item) => item.sleeve === sleeve) }));
      return `${this.stepHeader('Traditional cotton/poly shirts', `Choose ${required} white shirts`, 'Choose one fit, one collar size, and one long-sleeve length for the full package. Then divide the ten shirts between long and short sleeves.')}
        <div class="pms-missionary__form-grid">
          ${this.selectField('traditional-fit', 'Fit for all 10 shirts', fits.map((value) => ({ value, label: value })), this.state.traditionalFit)}
          ${this.selectField('traditional-collar', 'Collar size for all 10 shirts', collars.map((value) => ({ value, label: value })), this.state.traditionalCollar)}
          ${this.selectField('traditional-sleeve', 'Long-sleeve length', sleeves.map((value) => ({ value, label: value })), this.state.traditionalSleeve)}
        </div>
        ${this.counter(required, this.core.sumAllocation(this.state.shirts), 'shirts')}
        ${grouped.map((group) => group.choices.length ? `<section class="pms-missionary__subgroup"><h3>${escapeHtml(group.sleeve)}</h3><div class="pms-missionary__choice-grid">${group.choices.map((choice) => this.quantityCard('shirts', choice, required)).join('')}</div></section>` : '').join('')}
        ${this.navigation()}`;
    }

    renderTiesStep() {
      const required = this.packageDefinition.ties;
      const query = this.core.normalize(this.tieSearch);
      const all = this.core.getTieChoices(this.catalog.ties);
      const choices = query ? all.filter((choice) => choice.search.includes(query)) : all;
      return `${this.stepHeader('Washable ties', `Choose ${required} ties`, 'Choose your favorite colors and patterns. Search by color or style, and mix any six ties from this selection.')}
        <label class="pms-missionary__field"><span>Search ties</span><input type="search" data-field="tie-search" value="${escapeHtml(this.tieSearch)}" placeholder="Search pattern, color, or SKU"></label>
        ${this.counter(required, this.core.sumAllocation(this.state.ties), 'ties')}
        <div class="pms-missionary__choice-grid pms-missionary__choice-grid--images">${choices.map((choice) => this.quantityCard('ties', choice, required)).join('')}</div>
        ${this.navigation()}`;
    }

    renderSocksStep() {
      const required = this.packageDefinition.socks;
      const sizes = this.core.getSockSizes(this.catalog.socks);
      const choices = this.state.sockSize ? this.core.getSockChoices(this.catalog.socks, this.state.sockSize) : [];
      return `${this.stepHeader('Drymax performance socks', `Choose ${required} pairs`, 'Choose one sock size, then distribute ten pairs among any available colors.')}
        ${this.selectField('sock-size', 'Sock size', sizes.map((value) => ({ value, label: value })), this.state.sockSize)}
        ${this.counter(required, this.core.sumAllocation(this.state.socks), 'socks')}
        <div class="pms-missionary__choice-grid">${choices.map((choice) => this.quantityCard('socks', choice, required)).join('')}</div>
        ${this.navigation()}`;
    }

    renderBeltStep(index) {
      const belt = this.state.belts[index];
      const buckles = this.core.getSimpleVariantChoices(this.catalog.buckles, [/buckle/i, /style/i]).map((choice) => {
        const status = this.candidateInventory(choice.variant, 1, belt.buckleVariantId, 1);
        return { ...choice, available: status.allowed, inventoryNote: this.inventoryStatusText(status) };
      });
      const straps = this.core.getSimpleVariantChoices(this.catalog.straps, [/strap/i, /style/i, /colou?r/i]).map((choice) => {
        const status = this.candidateInventory(choice.variant, 1, belt.strapVariantId, 1);
        return { ...choice, available: status.allowed, inventoryNote: this.inventoryStatusText(status) };
      });
      const sizes = [];
      for (let size = Number(this.config.beltSizeMin || 28); size <= Number(this.config.beltSizeMax || 60); size += Number(this.config.beltSizeStep || 2)) sizes.push(String(size));
      return `${this.stepHeader(`Belt ${index + 1} of ${this.packageDefinition.belts}`, `Build Belt ${index + 1}`, 'Choose any eligible buckle and strap. Leaving the strap uncut is recommended until the final fit is confirmed.')}
        <section class="pms-missionary__belt-part">
          <h3>Choose a buckle</h3>
          <p>Select the buckle style by clicking its picture.</p>
          ${this.visualChoicePicker(`belt-buckle-${index}`, buckles, belt.buckleVariantId, 'No eligible buckles are currently available.')}
        </section>
        <section class="pms-missionary__belt-part">
          <h3>Choose a strap</h3>
          <p>Select the strap color and material by clicking its picture.</p>
          ${this.visualChoicePicker(`belt-strap-${index}`, straps, belt.strapVariantId, 'No eligible straps are currently available.')}
        </section>
        <fieldset class="pms-missionary__belt-sizing"><legend>A note about belt sizing</legend>
          <p>Belt straps can be left uncut or custom-cut. We recommend leaving the strap uncut unless you are confident of the final fit.</p>
          <label><input type="radio" name="belt-sizing-${index}" data-field="belt-sizing-${index}" value="uncut" ${belt.sizing !== 'cut' ? 'checked' : ''}> <span><strong>Leave strap uncut — recommended</strong><small>It can be sized in-store or at home and remains eligible for return under the normal policy.</small></span></label>
          <label><input type="radio" name="belt-sizing-${index}" data-field="belt-sizing-${index}" value="cut" ${belt.sizing === 'cut' ? 'checked' : ''}> <span><strong>Cut strap to size</strong><small>Custom-cut straps are final sale and cannot be returned or exchanged.</small></span></label>
          ${belt.sizing === 'cut' ? `<div class="pms-missionary__cut-panel">
            ${this.selectField(`belt-size-${index}`, 'Requested cut size', sizes.map((value) => ({ value, label: value })), belt.cutSize)}
            <label class="pms-missionary__ack"><input type="checkbox" data-field="belt-ack-${index}" ${belt.acknowledged ? 'checked' : ''}> I understand that this custom-cut belt will be final sale.</label>
          </div>` : ''}
        </fieldset>
        ${this.navigation()}`;
    }

    renderReviewStep() {
      const validation = this.core.validateState({ packageDefinition: this.packageDefinition, state: this.state, catalog: this.catalog });
      const inventory = this.inventoryReport();
      let plan = null; let retail = 0;
      try { plan = this.core.buildCartPlan({ packageDefinition: this.packageDefinition, state: this.state, catalog: this.catalog }); retail = this.core.calculateRetailTotal(plan, this.catalog); }
      catch (_) { /* validation explains missing pieces */ }
      const savings = Math.max(0, retail - this.packageDefinition.targetPrice);
      const handoffEnabled = this.config.enablePlanHandoff !== false;
      const handoffAction = this.contactFormAction();
      return `${this.stepHeader('Final review', 'Your missionary package', 'Review the choices, add optional contact information for the printed plan, then purchase online or print/save the plan for an in-store visit.')}
        ${this.state.customer.mission ? `<div class="pms-missionary__mission-review"><span>Prepared for service in</span><strong>${escapeHtml(this.missionDisplayName())}</strong></div>` : ''}
        ${!validation.valid ? `<div class="pms-missionary__error"><strong>Still needed:</strong> ${escapeHtml(validation.errors.join(' '))}</div>` : ''}
        ${this.shortageSummary(inventory, true)}
        <div class="pms-missionary__review-grid">
          <section class="pms-missionary__review-list">${this.reviewRows()}</section>
          <aside class="pms-missionary__price-box">
            <div><span>Regular retail</span><b>${this.money(retail)}</b></div>
            <div class="is-savings"><span>Package savings</span><b>${this.money(savings)}</b></div>
            <div class="is-total"><span>Final package price</span><b>${this.money(this.packageDefinition.targetPrice)}</b></div>
            <p>Package savings stay in place while all required quantities remain in your cart. Extras may be added at regular price.</p>
          </aside>
        </div>
        ${handoffEnabled ? `<form class="pms-missionary__handoff-form" data-pms-handoff-form method="post" action="${escapeHtml(handoffAction)}" target="_blank" accept-charset="UTF-8">
          <input type="hidden" name="form_type" value="contact">
          <input type="hidden" name="utf8" value="✓">
          <input type="hidden" name="contact[name]" value="" data-pms-handoff-field="contact-name">
          <input type="hidden" name="contact[mission]" value="" data-pms-handoff-field="mission">
          <input type="hidden" name="contact[package]" value="" data-pms-handoff-field="package">
          <input type="hidden" name="contact[plan_reference]" value="" data-pms-handoff-field="reference">
          <input type="hidden" name="contact[body]" value="" data-pms-handoff-field="body">` : ''}
        <section class="pms-missionary__customer"><h3>Your details <span>Optional unless sending the plan</span></h3><p>These details can appear on the printed plan and help our staff prepare if you choose to send it to Pomeroy's.</p><div class="pms-missionary__form-grid">
          ${this.textField('name', 'Missionary name', this.state.customer.name, 'text', handoffEnabled ? 'contact[missionary_name]' : '')}
          ${this.textField('contactName', 'Parent or contact name', this.state.customer.contactName, 'text', handoffEnabled ? 'contact[parent_or_contact_name]' : '')}
          ${this.textField('phone', 'Phone', this.state.customer.phone, 'tel', handoffEnabled ? 'contact[phone]' : '')}
          ${this.textField('email', 'Email', this.state.customer.email, 'email', handoffEnabled ? 'contact[email]' : '')}
        </div></section>
        ${handoffEnabled ? `<section class="pms-missionary__handoff" id="pms-plan-handoff">
          <div class="pms-missionary__handoff-copy">
            <p>Want us to have your choices ready?</p>
            <h3>Send Your Package Plan to Pomeroy's</h3>
            <div>We’ll receive your complete selections and contact details so our staff can review the plan before you visit. Sending the plan does not reserve inventory or require a purchase.</div>
          </div>
          <label class="pms-missionary__handoff-consent">
            <input type="checkbox" data-field="handoff-consent" name="contact[permission_to_contact]" value="Yes — customer checked the package-plan contact consent box." ${this.state.handoffConsent ? 'checked' : ''}>
            <span>${escapeHtml(this.config.handoffConsentLabel || "I agree that Pomeroy's may contact me about this package plan and fitting.")}</span>
          </label>
          <button type="button" class="button button--secondary" data-action="send-plan">${escapeHtml(this.state.handoffSubmitted ? 'Send Plan Again' : (this.config.handoffButtonLabel || "Send My Plan to Pomeroy's"))}</button>
          <div class="pms-missionary__handoff-error" data-pms-handoff-error role="alert" ${this.state.handoffError ? '' : 'hidden'}>${escapeHtml(this.state.handoffError || '')}</div>
          <div class="pms-missionary__handoff-status" data-pms-handoff-status data-confirmed="${Boolean(this.state.handoffConfirmed)}" role="status" ${this.state.handoffSubmitted ? '' : 'hidden'}><strong>${escapeHtml(this.state.handoffConfirmed ? this.config.handoffLabels.sentTitle : this.config.handoffLabels.pendingTitle)}</strong><span>${escapeHtml(this.state.handoffConfirmed ? this.config.handoffLabels.sentBody : this.config.handoffLabels.pendingBody)}</span></div>
          <small>Email and permission to contact you are required only when sending the plan. Printing, scheduling, and purchasing remain available without submitting it.</small>
        </section></form>` : ''}
        ${this.config.appointmentUrl ? `<section class="pms-missionary__appointment">
          <div class="pms-missionary__appointment-copy">
            <p>Prefer to finish in person?</p>
            <h3>Schedule a Missionary Package Appointment</h3>
            <div>Bring your completed plan and we’ll confirm the fit, review your choices, and help finish the package. Please allow 1–2 hours for a complete fitting.</div>
          </div>
          <div class="pms-missionary__appointment-actions">
            <button type="button" class="button button--secondary" data-action="copy-summary">Copy My Package Summary</button>
            <a class="button" href="${escapeHtml(this.config.appointmentUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(this.config.appointmentButtonLabel || 'Schedule My In-Store Appointment')}</a>
          </div>
          <small>Copy the summary first, then paste it into the optional notes field while booking so our staff can review your selections ahead of time.</small>
          ${this.copyFallback ? `<div class="pms-missionary__copy-fallback">
            <label><span>Package summary</span><textarea readonly data-pms-summary-fallback>${escapeHtml(this.copyFallback)}</textarea></label>
            <div><button type="button" class="button button--secondary" data-action="select-summary">Select Summary Text</button><small>Then press Ctrl+C (Windows) or Command+C (Mac).</small></div>
          </div>` : ''}
        </section>` : ''}
        <div class="pms-missionary__review-actions">
          <button type="button" class="button button--secondary" data-action="print">Print or Save My Package Plan</button>
          <button type="button" class="button" data-action="purchase" ${this.purchasing || !validation.valid || !this.config.enableOnlinePurchase || (inventory.toOrder > 0 && !this.state.shortageAcknowledged) ? 'disabled' : ''}>${this.purchasing ? 'Adding your package…' : escapeHtml(this.config.purchaseButtonLabel || 'Purchase Package Online')}</button>
        </div>
        ${!this.config.enableOnlinePurchase ? '<p class="pms-missionary__beta-note">Online cart assembly is disabled in this beta by default until package-price discount enforcement is connected. Printing is fully available.</p>' : ''}
        ${this.navigation(true)}`;
    }

    shortageSummary(inventory = this.inventoryReport(), includeAcknowledgment = false) {
      if (!inventory.toOrder) return '';
      const rows = inventory.shortages.map((line) => {
        const title = line.variant.productTitle || line.components[0] || line.variant.title || 'Package item';
        return `<li><strong>${escapeHtml(title)}</strong><span>${line.availableNow} available now - ${line.toOrder} to be ordered</span></li>`;
      }).join('');
      return `<section class="pms-missionary__shortage-summary">
        <div><p>Special-order items</p><h3>${inventory.toOrder} ${inventory.toOrder === 1 ? 'item needs' : 'items need'} to be ordered</h3><span>These selections can still be purchased because they are approved for continued selling in Shopify.</span></div>
        <ul>${rows}</ul>
        ${includeAcknowledgment ? `<label><input type="checkbox" data-field="shortage-ack" ${this.state.shortageAcknowledged ? 'checked' : ''}> <span>I understand that some selections must be ordered from Pomeroy's vendors and may require additional preparation time.</span></label>` : ''}
      </section>`;
    }

    reviewRows() {
      const rows = [];
      this.state.suits.slice(0, this.packageDefinition.suitCount).forEach((selection, index) => {
        const program = this.getSuitProgram(selection.programId);
        const coat = program?.coat.variants.find((variant) => variant.id === selection.coatVariantId);
        const pant = program?.pant.variants.find((variant) => variant.id === selection.pantVariantId);
        rows.push(this.reviewRow(`Suit ${index + 1}`, `${program?.title || 'Suit'} · ${selection.colorLabel || ''}`, [
          coat ? `Coat: ${coat._pmsSize}` : 'Coat not selected',
          pant ? `2 matching pants: ${pant._pmsSize}` : 'Pant not selected',
        ], `<div class="pms-missionary__suit-details">${this.detailsButton(program?.coat, coat, this.detailLabel('coat', 'Coat photos and details'))}${this.detailsButton(program?.pant, pant, this.detailLabel('pant', 'Pant photos and details'))}</div>`));
      });
      rows.push(this.allocationReview('Golf slacks', this.catalog.slacks, this.state.slacks));
      rows.push(this.allocationReview(this.packageDefinition.shirtFamily === 'traditional' ? 'Traditional shirts' : 'Stretch shirts', this.packageDefinition.shirtFamily === 'traditional' ? this.catalog.traditionalShirts : this.catalog.stretchShirts, this.state.shirts));
      rows.push(this.allocationReview('Ties', this.catalog.ties, this.state.ties));
      rows.push(this.allocationReview('Drymax socks', this.catalog.socks, this.state.socks));
      this.state.belts.forEach((belt, index) => {
        const buckle = this.core.selectedVariant(this.catalog.buckles, belt.buckleVariantId);
        const strap = this.core.selectedVariant(this.catalog.straps, belt.strapVariantId);
        rows.push(this.reviewRow(`Belt ${index + 1}`, `${buckle?.title || 'Buckle not selected'} + ${strap?.title || 'Strap not selected'}`, [belt.sizing === 'cut' ? `Cut to size ${belt.cutSize || '—'} · Final sale` : 'Leave strap uncut'], `<div class="pms-missionary__suit-details">${this.detailsButtonForVariant(belt.buckleVariantId, buckle?.productTitle)}${this.detailsButtonForVariant(belt.strapVariantId, strap?.productTitle)}</div>`));
      });
      rows.push(this.reviewRow('Included automatically', '1 sewing kit · 1 clothing care booklet', [], `<div class="pms-missionary__suit-details">${this.detailsButton(this.catalog.sewingKit, null, this.catalog.sewingKit?.title)}${this.detailsButton(this.catalog.careBooklet, null, this.catalog.careBooklet?.title)}</div>`));
      return rows.join('');
    }

    reviewRow(title, subtitle, details, productLinks = '') {
      return `<article><h3>${escapeHtml(title)}</h3><p>${escapeHtml(subtitle)}</p>${details.length ? `<ul>${details.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>` : ''}${productLinks}</article>`;
    }

    allocationReview(title, products, allocation) {
      const details = Object.entries(allocation || {}).map(([variantId, quantity]) => {
        const variant = this.core.selectedVariant(products, variantId);
        return variant ? `${quantity} × ${variant.productTitle} — ${variant.title}` : `${quantity} × Variant ${variantId}`;
      });
      const links = Object.entries(allocation || {}).filter(([, quantity]) => Number(quantity) > 0).map(([variantId]) => this.detailsButtonForVariant(variantId)).join('');
      return this.reviewRow(title, `${this.core.sumAllocation(allocation)} selected`, details, `<div class="pms-missionary__suit-details">${links}</div>`);
    }

    selectField(field, label, options, value) {
      const hasOptions = options && options.length;
      return `<label class="pms-missionary__field"><span>${escapeHtml(label)}</span><select data-field="${escapeHtml(field)}" ${!hasOptions ? 'disabled' : ''}>
        ${!value ? '<option value="">Choose…</option>' : ''}
        ${(options || []).map((option) => `<option value="${escapeHtml(option.value)}" ${String(option.value) === String(value) ? 'selected' : ''} ${option.disabled ? 'disabled' : ''}>${escapeHtml(option.label)}</option>`).join('')}
      </select></label>`;
    }

    visualChoicePicker(field, choices, value, emptyMessage) {
      if (!choices || !choices.length) {
        return `<div class="pms-missionary__callout">${escapeHtml(emptyMessage)}</div>`;
      }
      return `<div class="pms-missionary__visual-grid">
        ${choices.map((choice) => {
          const selected = String(choice.id) === String(value);
          const image = choice.image
            ? `<img src="${escapeHtml(choice.image)}" alt="${escapeHtml(choice.title)}" loading="lazy">`
            : '<span class="pms-missionary__visual-placeholder">No image</span>';
          return `<div class="pms-missionary__visual-item"><label class="pms-missionary__visual-choice ${selected ? 'is-selected' : ''} ${!choice.available ? 'is-unavailable' : ''}">
            <input type="radio" name="${escapeHtml(field)}" data-field="${escapeHtml(field)}" value="${escapeHtml(choice.id)}" ${selected ? 'checked' : ''} ${!choice.available ? 'disabled' : ''}>
            <span class="pms-missionary__visual-image">${image}</span>
            <span class="pms-missionary__visual-copy"><strong>${escapeHtml(choice.title)}</strong>${choice.subtitle ? `<small>${escapeHtml(choice.subtitle)}</small>` : ''}${choice.inventoryNote ? `<small class="pms-missionary__inventory-note">${escapeHtml(choice.inventoryNote)}</small>` : ''}</span>
            <span class="pms-missionary__visual-check" aria-hidden="true">&#10003;</span>
          </label>${this.detailsButtonForVariant(choice.id)}</div>`;
        }).join('')}
      </div>`;
    }

    textField(field, label, value, type = 'text', name = '') {
      return `<label class="pms-missionary__field"><span>${escapeHtml(label)}</span><input type="${type}" data-customer-field="${field}" ${name ? `name="${escapeHtml(name)}"` : ''} value="${escapeHtml(value)}"></label>`;
    }

    counter(required, selected, label) {
      return `<div class="pms-missionary__counter ${selected === required ? 'is-complete' : ''}"><strong>${selected} of ${required}</strong><span>${escapeHtml(label)} selected</span></div>`;
    }

    quantityCard(group, choice, required) {
      const allocation = this.state[group] || {};
      const quantity = Number(allocation[choice.id]) || 0;
      const total = this.core.sumAllocation(allocation);
      const currentDemand = this.demandForVariant(choice.id);
      const currentStatus = this.core.inventoryStatus(choice.variant, currentDemand);
      const nextStatus = this.candidateInventory(choice.variant, quantity + 1, choice.id, quantity);
      const canAdd = total < required && nextStatus.allowed;
      const inventoryNote = this.inventoryStatusText(currentStatus.requested > 0 ? currentStatus : this.core.inventoryStatus(choice.variant, 0));
      const image = choice.image ? `<img src="${escapeHtml(choice.image)}" alt="" loading="lazy">` : '<div class="pms-missionary__placeholder">P</div>';
      return `<article class="pms-missionary__choice ${!nextStatus.allowed && quantity <= 0 ? 'is-unavailable' : ''} ${currentStatus.toOrder > 0 ? 'needs-order' : ''}">
        <button type="button" class="pms-missionary__choice-image pms-missionary__photo-button" data-action="product-details" data-variant-id="${escapeHtml(choice.id)}" aria-label="${escapeHtml(this.detailLabel('view', 'View details'))}: ${escapeHtml(choice.title)}">${image}</button>
        <div class="pms-missionary__choice-copy"><h4>${escapeHtml(choice.title)}</h4><p>${escapeHtml(choice.subtitle || '')}</p><small>${escapeHtml(choice.detail || '')}</small>${inventoryNote ? `<span class="pms-missionary__inventory-note ${currentStatus.toOrder > 0 ? 'needs-order' : ''}">${escapeHtml(inventoryNote)}</span>` : ''}${this.detailsButtonForVariant(choice.id)}</div>
        <div class="pms-missionary__qty">
          <button type="button" data-action="quantity" data-group="${group}" data-variant-id="${choice.id}" data-delta="-1" ${quantity <= 0 ? 'disabled' : ''} aria-label="Decrease ${escapeHtml(choice.title)}">−</button>
          <span>${quantity}</span>
          <button type="button" data-action="quantity" data-group="${group}" data-variant-id="${choice.id}" data-delta="1" ${!canAdd ? 'disabled' : ''} aria-label="Increase ${escapeHtml(choice.title)}">+</button>
        </div>
      </article>`;
    }

    detailLabel(key, fallback) {
      return this.config?.productDetailsLabels?.[key] || fallback;
    }

    detailsAttributes(product, variant) {
      return `data-action="product-details" data-product-id="${escapeHtml(product.id)}" data-variant-id="${escapeHtml(variant?.id || '')}"`;
    }

    detailsButton(product, variant, label) {
      if (!product) return '';
      return `<button type="button" class="pms-missionary__details-button" ${this.detailsAttributes(product, variant)} aria-label="${escapeHtml(this.detailLabel('view', 'View details'))}: ${escapeHtml(product.title)}${variant?.title ? ` — ${escapeHtml(variant.title)}` : ''}">${escapeHtml(label || this.detailLabel('view', 'View details'))}</button>`;
    }

    detailsButtonForVariant(variantId, label) {
      const product = this.detailProducts?.find((item) => item.variants.some((variant) => String(variant.id) === String(variantId)));
      return product ? this.detailsButton(product, product.variants.find((variant) => String(variant.id) === String(variantId)), label) : '';
    }

    openProductDetails(button) {
      const variantId = button.dataset.variantId;
      const product = this.detailProducts.find((item) => button.dataset.productId
        ? String(item.id) === button.dataset.productId
        : item.variants.some((variant) => String(variant.id) === String(variantId)));
      if (!product || !window.PMSProductDetails) return;
      if (!this.productDetails) this.productDetails = new window.PMSProductDetails(this, this.config.productDetailsLabels);
      const variant = product.variants.find((item) => String(item.id) === String(variantId));
      this.productDetails.open(product, variant, button);
    }

    disconnectedCallback() {
      this.productDetails?.destroy();
      this.productDetails = null;
    }

    navigation(review = false) {
      return `<div class="pms-missionary__nav">
        <button type="button" class="button button--secondary" data-action="back" ${this.stepIndex === 0 ? 'disabled' : ''}>Back</button>
        ${review ? '' : '<button type="button" class="button" data-action="next">Continue</button>'}
      </div>
      ${this.status ? `<div class="pms-missionary__status ${this.statusError ? 'is-error' : ''}" aria-live="polite">${escapeHtml(this.status)}</div>` : ''}`;
    }

    changeQuantity(group, variantId, delta) {
      const requiredMap = { slacks: this.packageDefinition.slacks, shirts: this.packageDefinition.shirts, ties: this.packageDefinition.ties, socks: this.packageDefinition.socks };
      if (Number(delta) > 0) {
        const productMap = { slacks: this.catalog.slacks, shirts: this.packageDefinition.shirtFamily === 'traditional' ? this.catalog.traditionalShirts : this.catalog.stretchShirts, ties: this.catalog.ties, socks: this.catalog.socks };
        const variant = this.core.selectedVariant(productMap[group], variantId);
        const current = Number(this.state[group]?.[variantId]) || 0;
        if (!this.candidateInventory(variant, current + 1, variantId, current).allowed) return;
      }
      this.state[group] = this.core.updateAllocation(this.state[group], variantId, delta, requiredMap[group]);
      this.state.shortageAcknowledged = false;
      this.clearStatus();
      this.renderStepOnly();
    }

    isStepComplete(step) {
      if (step.type === 'suit') {
        const suit = this.state.suits[step.index];
        return Boolean(suit?.programId && suit?.colorKey && suit?.coatVariantId && suit?.pantVariantId);
      }
      if (step.type === 'slacks') return Boolean(this.state.slackWaist) && this.core.sumAllocation(this.state.slacks) === this.packageDefinition.slacks;
      if (step.type === 'shirts') {
        const sized = this.packageDefinition.shirtFamily === 'traditional'
          ? Boolean(this.state.traditionalFit && this.state.traditionalCollar && this.state.traditionalSleeve)
          : Boolean(this.state.shirtSize);
        return sized && this.core.sumAllocation(this.state.shirts) === this.packageDefinition.shirts;
      }
      if (step.type === 'ties') return this.core.sumAllocation(this.state.ties) === this.packageDefinition.ties;
      if (step.type === 'socks') return Boolean(this.state.sockSize) && this.core.sumAllocation(this.state.socks) === this.packageDefinition.socks;
      if (step.type === 'belt') {
        const belt = this.state.belts[step.index];
        return Boolean(belt?.buckleVariantId && belt?.strapVariantId && (belt.sizing !== 'cut' || (belt.cutSize && belt.acknowledged)));
      }
      if (step.type === 'review') return this.core.validateState({ packageDefinition: this.packageDefinition, state: this.state, catalog: this.catalog }).valid;
      return false;
    }

    validateCurrentStep() {
      const step = this.steps[this.stepIndex];
      if (this.isStepComplete(step)) return { valid: true, message: '' };
      const messages = {
        suit: 'Choose an available suit program, color, coat size, and pant waist. Two matching pants must be available.',
        slacks: `Choose one waist size and exactly ${this.packageDefinition.slacks} slacks.`,
        shirts: this.packageDefinition.shirtFamily === 'traditional'
          ? `Choose one fit, collar size, long-sleeve length, and exactly ${this.packageDefinition.shirts} shirts.`
          : `Choose one size family and exactly ${this.packageDefinition.shirts} shirts.`,
        ties: `Choose exactly ${this.packageDefinition.ties} ties.`,
        socks: `Choose one size and exactly ${this.packageDefinition.socks} pairs of socks.`,
        belt: 'Choose a buckle and strap. If the strap will be cut, choose the size and acknowledge the final-sale warning.',
      };
      return { valid: false, message: messages[step.type] || 'Complete this step before continuing.' };
    }

    goNext() {
      const validation = this.validateCurrentStep();
      if (!validation.valid) { this.setStatus(validation.message, true); this.renderStepOnly(); return; }
      this.clearStatus();
      this.stepIndex = Math.min(this.steps.length - 1, this.stepIndex + 1);
      this.renderStepOnly();
      this.scrollToTop();
    }

    goBack() {
      this.clearStatus();
      this.stepIndex = Math.max(0, this.stepIndex - 1);
      this.renderStepOnly();
      this.scrollToTop();
    }

    goToStep(index) {
      if (!Number.isInteger(index) || index < 0 || index >= this.steps.length) return;
      if (index > this.stepIndex) {
        for (let i = this.stepIndex; i < index; i += 1) {
          if (!this.isStepComplete(this.steps[i])) return;
        }
      }
      this.stepIndex = index;
      this.clearStatus();
      this.renderStepOnly();
      this.scrollToTop();
    }

    scrollToTop() {
      const target = this.stepContainer;
      if (!target) return;

      window.requestAnimationFrame(() => {
        const header = document.querySelector('sticky-header, .header-wrapper');
        const headerHeight = header?.getBoundingClientRect().height || 0;
        const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

        window.scrollTo({ top: Math.max(0, top), behavior });
      });
    }

    setStatus(message, error = false) { this.status = message; this.statusError = error; }
    clearStatus() { this.status = ''; this.statusError = false; }

    createHandoffReference() {
      const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const random = Math.random().toString(36).slice(2, 7).toUpperCase();
      return `PMS-PLAN-${date}-${random}`;
    }

    contactFormAction() {
      const root = String(window.Shopify?.routes?.root || '/');
      return `${root.endsWith('/') ? root : `${root}/`}contact#contact_form`;
    }

    syncHandoffVisualState() {
      if (!this.state.handoffSubmitted) this.state.handoffConfirmed = false;
      const button = this.app?.querySelector('[data-action="send-plan"]');
      const status = this.app?.querySelector('[data-pms-handoff-status]');
      const error = this.app?.querySelector('[data-pms-handoff-error]');
      if (button) button.textContent = this.state.handoffSubmitted ? 'Send Plan Again' : (this.config.handoffButtonLabel || "Send My Plan to Pomeroy's");
      if (status) {
        status.hidden = !this.state.handoffSubmitted;
        status.dataset.confirmed = String(Boolean(this.state.handoffConfirmed));
        status.querySelector('strong').textContent = this.state.handoffConfirmed ? this.config.handoffLabels.sentTitle : this.config.handoffLabels.pendingTitle;
        status.querySelector('span').textContent = this.state.handoffConfirmed ? this.config.handoffLabels.sentBody : this.config.handoffLabels.pendingBody;
      }
      if (error) {
        error.textContent = this.state.handoffError || '';
        error.hidden = !this.state.handoffError;
      }
    }

    showHandoffError(message, customerField = '') {
      this.state.handoffError = message;
      this.state.handoffSubmitted = false;
      this.syncHandoffVisualState();

      const field = customerField ? this.app?.querySelector(`[data-customer-field="${customerField}"]`) : null;
      if (field) {
        field.setAttribute('aria-invalid', 'true');
        field.focus({ preventScroll: true });
        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    sendPackagePlan() {
      const validation = this.core.validateState({ packageDefinition: this.packageDefinition, state: this.state, catalog: this.catalog });
      if (!validation.valid) {
        this.setStatus('Please complete the package before sending the plan.', true);
        this.renderStepOnly();
        return;
      }

      const requiredContactFields = [
        { key: 'name', label: 'missionary name' },
        { key: 'contactName', label: 'parent or contact name' },
        { key: 'phone', label: 'phone' },
        { key: 'email', label: 'email' },
      ];
      const missingContactFields = requiredContactFields.filter(({ key }) => !String(this.state.customer[key] || '').trim());
      if (missingContactFields.length) {
        const labels = missingContactFields.map(({ label }) => label);
        const readableList = labels.length === 1
          ? labels[0]
          : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
        this.showHandoffError(`Please complete the ${readableList} before sending your package plan.`, missingContactFields[0].key);
        return;
      }

      const email = String(this.state.customer.email || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.showHandoffError('Please enter a valid email address before sending your package plan.', 'email');
        return;
      }
      if (!this.state.handoffConsent) {
        this.showHandoffError('Please check the permission box before sending your package plan.');
        return;
      }

      this.state.handoffError = '';
      this.syncHandoffVisualState();

      const reference = this.state.handoffReference || this.createHandoffReference();
      const targetName = `pms_plan_${reference.replace(/[^a-z0-9]/gi, '_')}`;
      const verificationWindow = window.open('', targetName);
      if (!verificationWindow) {
        this.setStatus('Your browser blocked the plan-submission window. Allow pop-ups for this page and try again.', true);
        this.renderStepOnly();
        return;
      }

      try {
        verificationWindow.document.open();
        this.handoffWindow = verificationWindow;
        this.state.handoffConfirmed = false;
        verificationWindow.document.write('<!doctype html><title>Sending package plan...</title><p style="font:18px Arial,sans-serif;padding:32px">Sending your package plan securely to Pomeroy\'s...</p>');
        verificationWindow.document.close();

        const form = this.app.querySelector('[data-pms-handoff-form]');
        if (!form) throw new Error('The Shopify contact form is not available.');
        form.target = targetName;
        const contactName = String(this.state.customer.contactName || '').trim();
        const missionaryName = String(this.state.customer.name || '').trim();
        const values = {
          'contact-name': contactName || missionaryName || 'Missionary Package Builder customer',
          mission: this.missionDisplayName(),
          package: this.packageDefinition.title,
          reference,
          body: `MISSIONARY PACKAGE PLAN\nReference: ${reference}\n\n${this.packageSummaryText()}`,
        };
        Object.entries(values).forEach(([key, value]) => {
          const field = form.querySelector(`[data-pms-handoff-field="${key}"]`);
          if (field) field.value = String(value || '');
        });
        form.requestSubmit();

        this.state.handoffReference = reference;
        this.state.handoffSubmitted = true;
        this.setStatus(`Package plan ${reference} opened for secure submission to Pomeroy's.`);
        this.syncHandoffVisualState();
      } catch (error) {
        try { verificationWindow.close(); } catch (_) {}
        this.handoffWindow = null;
        console.error('[PMS Missionary Builder] Plan handoff failed.', error);
        this.setStatus('The package plan could not be opened for submission. Please print the plan or schedule an appointment instead.', true);
        this.renderStepOnly();
      }
    }

    async purchasePackage() {
      if (!this.config.enableOnlinePurchase || this.purchasing) return;
      this.purchasing = true;
      try {
        const inventory = this.inventoryReport();
        if (inventory.toOrder > 0 && !this.state.shortageAcknowledged) {
          throw new Error('Please acknowledge the special-order notice before purchasing the package.');
        }
        const plan = this.core.buildCartPlan({ packageDefinition: this.packageDefinition, state: this.state, catalog: this.catalog });
        this.setStatus('Adding the complete package to the cart…');
        this.renderStepOnly();
        await this.adapter.addCartPlan(plan, this.config.afterAdd || 'cart');
      } catch (error) {
        console.error('[PMS Missionary Builder] Cart add failed.', error);
        this.purchasing = false;
        this.setStatus(error.message || 'The package could not be added. Please review the selections.', true);
        this.renderStepOnly();
      }
    }

    async clearTestCart() {
      if (!this.config.designMode) return;
      try {
        this.setStatus('Clearing this browser\'s preview cart...');
        this.renderStepOnly();
        await this.adapter.clearCart();
        window.location.reload();
      } catch (error) {
        console.error('[PMS Missionary Builder] Cart clear failed.', error);
        this.setStatus(error.message || 'The preview cart could not be cleared.', true);
        this.renderStepOnly();
      }
    }

    requiresStandalonePrintDocument() {
      const userAgent = String(window.navigator?.userAgent || '');
      const platform = String(window.navigator?.platform || '');
      const touchPoints = Number(window.navigator?.maxTouchPoints || 0);
      const isIOS = /iPad|iPhone|iPod/i.test(userAgent) || (platform === 'MacIntel' && touchPoints > 1);
      const isAndroid = /Android/i.test(userAgent);
      return isIOS || isAndroid;
    }

    printPlanInStandaloneWindow() {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        this.setStatus('Your browser blocked the print window. Allow pop-ups for this page and try again.', true);
        this.renderStepOnly();
        return;
      }

      const planHtml = this.printContainer ? this.printContainer.innerHTML : '';
      const documentTitle = `${this.packageDefinition.title} - Package Plan`;
      printWindow.document.open();
      printWindow.document.write(`<!doctype html>
        <html lang="en">
          <head>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1">
            <title>${escapeHtml(documentTitle)}</title>
            <style>
              @page { size: letter portrait; margin: .28in; }
              * { box-sizing: border-box; }
              html, body { width: auto; height: auto; min-height: 0; margin: 0; padding: 0; background: #fff; }
              body { color: #111; font: 8pt/1.16 Arial, Helvetica, sans-serif; }
              header { display: flex; justify-content: space-between; gap: .18in; align-items: flex-start; border-bottom: 1.5pt solid #111; padding-bottom: .07in; }
              h1 { margin: 0; font-size: 17pt; line-height: 1.02; }
              header > p { margin: .03in 0 0; white-space: nowrap; }
              .pms-missionary__print-mission { margin: .04in 0 0; font-size: 10pt; font-weight: 700; }
              .pms-missionary__print-contact { display: flex; gap: .18in; margin: .08in 0 .1in; }
              .pms-missionary__print-rows { columns: 2; column-gap: .16in; }
              article { display: inline-block; width: 100%; break-inside: avoid; page-break-inside: avoid; margin: 0 0 .07in; border: .75pt solid #999; padding: .065in; vertical-align: top; }
              article h3 { margin: 0 0 .025in; font-size: 9.5pt; line-height: 1.05; }
              article p { margin: .02in 0; }
              article ul { margin: .025in 0 0; padding-left: .14in; }
              article li { margin: 0; }
              .pms-missionary__shortage-summary { margin: .08in 0; padding: .065in; border: .75pt solid #777; background: #f3f3f3; break-inside: avoid; page-break-inside: avoid; }
              .pms-missionary__shortage-summary h3 { margin: 0 0 .025in; font-size: 9.5pt; }
              .pms-missionary__shortage-summary p,
              .pms-missionary__shortage-summary ul { margin: .02in 0; }
              footer { margin-top: .08in; border-top: 1.5pt solid #111; padding-top: .055in; break-inside: avoid; page-break-inside: avoid; }
              footer p { margin: .025in 0 0; }
              @media screen {
                body { max-width: 8in; margin: .2in auto; padding: .15in; }
              }
            </style>
          </head>
          <body>${planHtml}</body>
        </html>`);
      printWindow.document.close();

      const closePrintWindow = () => {
        window.setTimeout(() => {
          try { printWindow.close(); } catch (_) {}
        }, 250);
      };

      printWindow.addEventListener('afterprint', closePrintWindow, { once: true });
      printWindow.setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 250);
    }

    async printPlan() {
      this.updatePrintSummary();

      if (this.requiresStandalonePrintDocument()) {
        this.printPlanInStandaloneWindow();
        return;
      }

      let printPortal = document.querySelector('[data-pms-print-portal]');
      if (!printPortal) {
        printPortal = document.createElement('section');
        printPortal.className = 'pms-missionary__print pms-missionary__print-portal';
        printPortal.setAttribute('data-pms-print-portal', '');
        printPortal.setAttribute('aria-hidden', 'true');
        document.body.appendChild(printPortal);
      }

      printPortal.innerHTML = this.printContainer ? this.printContainer.innerHTML : '';
      const body = document.body;
      const livePage = document.createDocumentFragment();
      const liveNodes = Array.from(body.childNodes).filter((node) => node !== printPortal);
      liveNodes.forEach((node) => livePage.appendChild(node));
      body.classList.add('pms-missionary-printing');

      let restored = false;
      let printViewHidden = false;
      let printViewBlurred = false;
      let restoreTimer = null;

      const restoreLivePage = () => {
        if (restored) return;
        restored = true;
        if (restoreTimer) window.clearTimeout(restoreTimer);
        window.removeEventListener('afterprint', restoreLivePage);
        window.removeEventListener('blur', handlePrintBlur);
        window.removeEventListener('focus', handlePrintFocus);
        document.removeEventListener('visibilitychange', handlePrintVisibility);
        body.classList.remove('pms-missionary-printing');
        if (printPortal.parentNode === body) {
          body.insertBefore(livePage, printPortal);
        } else {
          body.appendChild(livePage);
        }
      };

      const handlePrintVisibility = () => {
        if (document.visibilityState === 'hidden') {
          printViewHidden = true;
        } else if (printViewHidden) {
          window.setTimeout(restoreLivePage, 250);
        }
      };

      const handlePrintBlur = () => {
        printViewBlurred = true;
      };

      const handlePrintFocus = () => {
        if (printViewBlurred) window.setTimeout(restoreLivePage, 250);
      };

      window.addEventListener('afterprint', restoreLivePage);
      window.addEventListener('blur', handlePrintBlur);
      window.addEventListener('focus', handlePrintFocus);
      document.addEventListener('visibilitychange', handlePrintVisibility);

      // Android Chrome can snapshot the page for printing before a newly
      // inserted print portal has completed layout. The live storefront is
      // temporarily detached above so Android cannot retain its scroll height.
      // Force a measurement, then allow two paint cycles and a short settling
      // delay before opening the system print dialog.
      printPortal.getBoundingClientRect();
      await new Promise((resolve) => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(resolve);
        });
      });
      await new Promise((resolve) => window.setTimeout(resolve, 120));

      try {
        window.print();
        // Some Android print services do not emit afterprint. The printable
        // snapshot has already been created by this point, so this is a safe
        // final fallback for restoring the interactive storefront.
        if (!restored) restoreTimer = window.setTimeout(restoreLivePage, 30000);
      } catch (error) {
        restoreLivePage();
        throw error;
      }
    }

    packageSummaryText() {
      const lines = [
        "Pomeroy's Missionary Package Plan",
        `Package: ${this.packageDefinition.title}`,
        `Package price: ${this.money(this.packageDefinition.targetPrice)}`,
      ];
      if (this.state.customer.mission) lines.push(`Mission: ${this.missionDisplayName()}`);
      if (this.state.customer.name) lines.push(`Missionary: ${this.state.customer.name}`);
      if (this.state.customer.contactName) lines.push(`Parent/contact: ${this.state.customer.contactName}`);
      if (this.state.customer.phone) lines.push(`Phone: ${this.state.customer.phone}`);
      if (this.state.customer.email) lines.push(`Email: ${this.state.customer.email}`);
      lines.push('');

      this.state.suits.slice(0, this.packageDefinition.suitCount).forEach((selection, index) => {
        const program = this.getSuitProgram(selection.programId);
        const coat = program?.coat.variants.find((variant) => variant.id === selection.coatVariantId);
        const pant = program?.pant.variants.find((variant) => variant.id === selection.pantVariantId);
        lines.push(`Suit ${index + 1}: ${program?.title || 'Suit'} - ${selection.colorLabel || 'Color not selected'}`);
        lines.push(`  Coat: ${coat?._pmsSize || 'Not selected'}`);
        lines.push(`  2 matching pants: ${pant?._pmsSize || 'Not selected'}`);
      });

      this.allocationSummaryLines(lines, 'Golf slacks', this.catalog.slacks, this.state.slacks);
      this.allocationSummaryLines(lines, this.packageDefinition.shirtFamily === 'traditional' ? 'Traditional shirts' : 'Stretch shirts', this.packageDefinition.shirtFamily === 'traditional' ? this.catalog.traditionalShirts : this.catalog.stretchShirts, this.state.shirts);
      this.allocationSummaryLines(lines, 'Ties', this.catalog.ties, this.state.ties);
      this.allocationSummaryLines(lines, 'Drymax socks', this.catalog.socks, this.state.socks);

      this.state.belts.forEach((belt, index) => {
        const buckle = this.core.selectedVariant(this.catalog.buckles, belt.buckleVariantId);
        const strap = this.core.selectedVariant(this.catalog.straps, belt.strapVariantId);
        lines.push(`Belt ${index + 1}: ${buckle?.title || 'Buckle not selected'} + ${strap?.title || 'Strap not selected'}`);
        lines.push(`  Sizing: ${belt.sizing === 'cut' ? `Cut to ${belt.cutSize || 'not selected'} (final sale)` : 'Leave uncut'}`);
      });

      lines.push('Included: 1 sewing kit and 1 clothing care booklet');
      const inventory = this.inventoryReport();
      if (inventory.toOrder > 0) {
        lines.push('');
        lines.push(`SPECIAL-ORDER ITEMS (${inventory.toOrder} total):`);
        inventory.shortages.forEach((line) => {
          const title = line.variant.productTitle || line.components[0] || line.variant.title || 'Package item';
          lines.push(`  ${title}: ${line.availableNow} available now; ${line.toOrder} to be ordered`);
        });
      }
      lines.push(`Prepared: ${new Date().toLocaleDateString()}`);
      lines.push('Inventory is not reserved until purchased. Final sizing will be confirmed in store.');
      return lines.join('\n');
    }

    allocationSummaryLines(lines, title, products, allocation) {
      lines.push(`${title}:`);
      Object.entries(allocation || {}).forEach(([variantId, quantity]) => {
        const variant = this.core.selectedVariant(products, variantId);
        lines.push(`  ${quantity} x ${variant ? `${variant.productTitle} - ${variant.title}` : `Variant ${variantId}`}`);
      });
    }

    async copyPackageSummary() {
      const summary = this.packageSummaryText();
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(summary);
        } else {
          const field = document.createElement('textarea');
          field.value = summary;
          field.setAttribute('readonly', '');
          field.style.position = 'fixed';
          field.style.opacity = '0';
          document.body.appendChild(field);
          field.select();
          if (!document.execCommand('copy')) throw new Error('Copy was not available.');
          field.remove();
        }
        this.copyFallback = '';
        this.setStatus('Package summary copied. Paste it into the optional notes field while booking your appointment.');
      } catch (error) {
        console.error('[PMS Missionary Builder] Copy failed.', error);
        this.copyFallback = summary;
        this.setStatus('Automatic copying is blocked in this preview or browser. Select the summary below and copy it manually.');
      }
      this.renderStepOnly();
    }

    selectSummaryFallback() {
      const field = this.app.querySelector('[data-pms-summary-fallback]');
      if (!field) return;
      field.focus();
      field.select();
      field.setSelectionRange(0, field.value.length);
    }

    updatePrintSummary() {
      if (!this.printContainer) return;
      this.printContainer.innerHTML = `
        <header><div><h1>${escapeHtml(this.packageDefinition.title)}</h1>${this.state.customer.mission ? `<p class="pms-missionary__print-mission">Prepared for service in the ${escapeHtml(this.missionDisplayName())}</p>` : ''}</div><p>Prepared ${new Date().toLocaleDateString()}</p></header>
        <div class="pms-missionary__print-contact">
          ${this.state.customer.name ? `<strong>${escapeHtml(this.state.customer.name)}</strong>` : ''}
          ${this.state.customer.contactName ? `<span>${escapeHtml(this.state.customer.contactName)}</span>` : ''}
          ${this.state.customer.phone ? `<span>${escapeHtml(this.state.customer.phone)}</span>` : ''}
          ${this.state.customer.email ? `<span>${escapeHtml(this.state.customer.email)}</span>` : ''}
        </div>
        <div class="pms-missionary__print-rows">${this.reviewRows()}</div>
        ${this.shortageSummary(this.inventoryReport(), false)}
        <footer><strong>Package price: ${this.money(this.packageDefinition.targetPrice)}</strong><p>Inventory is not reserved until purchased. Bring this plan to Pomeroy’s for fitting and final confirmation.</p></footer>
      `;
    }

    missionDisplayName() {
      const mission = String(this.state.customer.mission || '').trim();
      if (!mission) return '';
      return /\bmission$/i.test(mission) ? mission : `${mission} Mission`;
    }

    money(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.config.currency || 'USD' }).format((Number(cents) || 0) / 100);
      } catch (_) { return `$${((Number(cents) || 0) / 100).toFixed(2)}`; }
    }

    showFatal(error) {
      console.error('[PMS Missionary Builder] Initialization failed.', error);
      const app = this.querySelector('[data-pms-missionary-app]');
      if (app) app.innerHTML = `<div class="pms-missionary__error"><strong>The package builder could not be loaded.</strong><br>${escapeHtml(error.message || 'Please refresh the page or contact the store.')}</div>`;
    }
  }

  if (!customElements.get('pms-missionary-builder')) {
    customElements.define('pms-missionary-builder', PMSMissionaryBuilder);
  }
})();
